import { CellContext, ColumnDefTemplate, Header, Row, Table } from '@tanstack/react-table';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { downloadAsXlsx } from '../../../spreadsheet-to-excel';
import { ColumnV2 } from '../../types';
import { extractTextContentFromTableComponent, extractTextContentOnly } from './cell-to-text';

type Exporter<TData extends object, TValue = any> = (
  datum: TValue,
  rowData: TData,
  cellContext: CellContext<TData, TValue>,
) => string | number | null | undefined;

type ExporterConfig<TData extends object, TValue> =
  | Exporter<TData, TValue>
  | false
  | 'value'
  | 'textContent'
  | undefined;

type ExportableSheet = SheetRow[];
type SheetRow = { [columnId: string]: number | string | null | undefined };

/**
 * Hook that returns a single callback that handles the everything from column definition to downloading the readied file.
 *
 * Only supports xlsx at the moment, but adding csv support should be easy.
 */
export function useTableV2Export<T extends object>(
  columns: Array<ColumnV2<T>>,
  table: Table<T>,
  options?: { filename?: string; enableExport?: false | 'definedExporters' | 'all' },
) {
  const exportData = useMemo(() => {
    if (options?.enableExport === false) {
      return undefined;
    }
    // Preprocess the column definitions and resolve options
    const exportedColumns = getColumnsToExport(
      columns,
      options?.enableExport ?? 'definedExporters',
      table.getLeafHeaders(),
    );
    if (exportedColumns.length === 0) {
      return undefined;
    }

    return () => {
      // Prepare the sheet, i.e. run the exporter functions for every cell
      const sheet = toExportableSheet(exportedColumns, table.getPrePaginationRowModel().rows);
      const filename = formFilename(options?.filename || 'data');
      // Save.
      downloadAsXlsx({ data: sheet }, filename);
    };
  }, [columns, table, options?.filename, options?.enableExport]);

  return exportData;
}

function getColumnsToExport<T extends object, V>(
  columns: Array<ColumnV2<T, V>>,
  enableExport: false | 'definedExporters' | 'all',
  headers: Array<Header<T, V>>,
) {
  if (enableExport === false) {
    return [];
  }

  const headersByColumnId = keyBy(headers, h => h.column.id);
  return columns.flatMap(column => {
    const exporter = buildExporter(enableExport, column.meta?.exportCell, column.cell);
    if (!exporter) {
      return [];
    } else {
      return [
        {
          id: column.id,
          header: column.meta?.exportColumnHeader ?? (getTextFromHeader(headersByColumnId[column.id]) || column.id),
          exporter,
        },
      ];
    }
  });
}

function buildExporter<T extends object, V>(
  enableExportOption: 'definedExporters' | 'all',
  exporter: ExporterConfig<T, V>,
  cell: ColumnDefTemplate<CellContext<T, V>> | undefined,
): Exporter<T, V> | undefined {
  if (exporter === false) {
    return undefined;
  }
  if (exporter === 'value') {
    return identityExporter;
  }
  if (typeof exporter === 'function') {
    return exporter;
  }
  if (exporter === 'textContent' && cell) {
    return extractTextContentOnly(cell);
  }

  // When exporter is not configured, the default behaviour depends on the passed option
  return getDefaultExporter(enableExportOption);
}

function getDefaultExporter(enableExportOption: 'definedExporters' | 'all') {
  if (enableExportOption === 'all') {
    // Mode which defaults to exporting with default exporter, and requires exporter: false to skip
    return identityExporter;
  }
  // Mode which requires exporter to be defined (either explicit or 'true' for defaults)
  return undefined;
}

const identityExporter = <TValue,>(value: TValue) => {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'function' ||
    typeof value === 'object' ||
    typeof value === 'symbol'
  ) {
    return '';
  }
  return String(value);
};

function toExportableSheet<TData extends object, TValue>(
  columnDefs: Array<{ id: string; header: string; exporter: Exporter<TData, TValue> }>,
  rows: Array<Row<TData>>,
): ExportableSheet {
  return rows.map(row => toExportableRow(columnDefs, row));
}

function toExportableRow<TData extends object, TValue>(
  columnDefs: Array<{ id: string; header: string; exporter: Exporter<TData, TValue> }>,
  row: Row<TData>,
): SheetRow {
  const cellsByColumnId = keyBy(row.getAllCells(), c => c.column.id);
  return columnDefs.reduce<SheetRow>((result, column) => {
    const rawValue = row.getValue<TValue>(column.id);
    const cellContext = cellsByColumnId[column.id].getContext() as CellContext<TData, TValue>;
    try {
      result[column.header] = column.exporter(rawValue, row.original, cellContext);
    } catch (e) {
      console.error('Exporter got an error in column:', column.id);
      throw e;
    }
    return result;
  }, {});
}

function formFilename(filenameBase: string) {
  const currentTime = new Date();
  const timestamp = `${currentTime.getFullYear()}${
    currentTime.getMonth() + 1
  }${currentTime.getDate()}-${currentTime.getHours()}${currentTime.getMinutes()}${currentTime.getSeconds()}`;
  // TODO: Could make this more robust against providing an extension in filename
  return `${filenameBase}-${timestamp}`;
}

function getTextFromHeader(header: Header<any, any>) {
  const columnDef = header.column.columnDef;
  const headerDef = columnDef.header;

  if (!headerDef) return undefined;
  if (typeof headerDef == 'string') return headerDef;

  return extractTextContentFromTableComponent(headerDef, header.getContext());
}
